(function() {
  const API_BASE_URL = 'https://shopify-app.sanaullaonline.pk';
  const SHOP_DOMAIN = window.Shopify?.shop || '{{ shop.permanent_domain }}';
  
  let isUpdating = false;

  const formatDate = (dateObj) => {
    return dateObj.toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  };

  const addDays = (date, days) => {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
  };

  const extractEndDate = (value) => {
    try {
      const paren = /\(([^)]+)\)/.exec(value);
      const source = paren ? paren[1] : value;
      const parts = source.split("-").map((p) => p.trim());
      const endStr = parts.length === 2 ? parts[1] : parts[0];
      const d = new Date(endStr);
      return isFinite(d) ? d : null;
    } catch (error) {
      return null;
    }
  };

  const calculateStitchingDays = (stitchingType, itemCount) => {
    if (stitchingType === 'none' || itemCount === 0) return 0;

    const standardStitchingDays = {
      2: 0, 4: 3, 6: 5, 10: 7, 15: 9, 20: 12, 100: 19
    };

    const expressStitchingDays = {
      2: 0, 4: 2, 6: 4, 10: 6, 15: 8, 30: 12, 100: 15
    };

    const daysMap = stitchingType === 'standard' ? standardStitchingDays : expressStitchingDays;
    const tiers = Object.keys(daysMap).map(Number).sort((a, b) => a - b);
    
    if (itemCount < tiers[0]) return 0;
    
    for (let i = 0; i < tiers.length; i++) {
      if (itemCount <= tiers[i]) {
        return daysMap[tiers[i]];
      }
    }
    
    return daysMap[tiers[tiers.length - 1]];
  };

  const getStitchingBreakdown = (standardCount, expressCount) => {
    if (standardCount === 0 && expressCount === 0) return null;
    const parts = [];
    if (standardCount > 0) parts.push(`${standardCount} standard`);
    if (expressCount > 0) parts.push(`${expressCount} express`);
    return parts.join(', ');
  };

  function computeMaxEstimatedDate(lines) {
    const DATE_KEYS = ["_Estimated Dispatch Date", "Estimated Dispatch Date"];
    const STITCH_KEYS = ["_Stitching", "Stitching", "stitching"];
    const CUSTOMIZATION_KEYS = [
      "Add ons", "Addon", "Addons", "Color", "Embellishments", "Embellishment", 
      "Get it Stitched", "Lining", "Size", "Stitching Options", "Stitching Service", 
      "Stitching Style", "Stitching Type", "style"
    ]; 

    let maxEnd = null;
    let standardStitchedCount = 0;
    let expressStitchedCount = 0;
    let hasStitching = false;
    let hasCustomization = false;
    let customizationBreakdown = [];

    for (const line of lines) {
      try {
        const props = line.attributes || [];
        const dateVal = props.find(p => DATE_KEYS.includes(p.key))?.value;
        const stitchVal = props.find(p => STITCH_KEYS.includes(p.key))?.value;
        const customizationVal = props.find(p => CUSTOMIZATION_KEYS.includes(p.key))?.value;

        if (stitchVal) {
          if (stitchVal.includes('standard stitching')) {
            standardStitchedCount += line.quantity || 1;
            hasStitching = true;
          } else if (stitchVal.includes('express stitching')) {
            expressStitchedCount += line.quantity || 1;
            hasStitching = true;
          }
        }

        if (customizationVal) {
          hasCustomization = true;
          customizationBreakdown.push(customizationVal);
        }

        if (dateVal) {
          const endDate = extractEndDate(dateVal);
          if (endDate) {
            if (!maxEnd || endDate > maxEnd) {
              maxEnd = endDate;
            }
          }
        }
      } catch (error) {
        console.error('Error processing line:', error);
      }
    }

    if (!maxEnd) {
      return {
        maxDateStr: "",
        stitchedCount: standardStitchedCount + expressStitchedCount,
        stitchingBreakdown: getStitchingBreakdown(standardStitchedCount, expressStitchedCount),
        hasStitching,
        hasCustomization,
        customizationBreakdown: customizationBreakdown.join(', ')
      };
    }

    const hasBothTypes = standardStitchedCount > 0 && expressStitchedCount > 0;
    const stitchingType = hasBothTypes ? 'standard' : 
                         (expressStitchedCount > 0 ? 'express' : 
                         (standardStitchedCount > 0 ? 'standard' : 'none'));
    
    const totalStitchedCount = hasBothTypes ? standardStitchedCount : (standardStitchedCount + expressStitchedCount);
    const extraDays = calculateStitchingDays(stitchingType, totalStitchedCount);

    const finalEnd = new Date(maxEnd);
    finalEnd.setDate(finalEnd.getDate() + extraDays);

    return {
      maxDateStr: formatDate(finalEnd),
      stitchedCount: totalStitchedCount,
      stitchingBreakdown: getStitchingBreakdown(standardStitchedCount, expressStitchedCount),
      hasStitching,
      hasCustomization,
      customizationBreakdown: customizationBreakdown.join(', ')
    };
  }

  async function fetchProductData(productId, shop) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/product-by-id?id=${encodeURIComponent(productId)}&shop=${encodeURIComponent(shop)}`
      );
      
      if (!response.ok) {
        console.error(`Failed to fetch product ${productId}:`, response.status);
        return null;
      }
      
      const data = await response.json();
      return data?.product || null;
    } catch (error) {
      console.error(`Error fetching product ${productId}:`, error);
      return null;
    }
  }

  async function calculateItemDate(item) {
    const currentStitching = item.properties?.['_Stitching'] || 
                            item.properties?.['Stitching'] || 
                            'none';

    const productId = item.product_id ? `gid://shopify/Product/${item.product_id}` : null;
    
    if (!productId) {
      console.error('No product ID for item:', item);
      return null;
    }

    const productData = await fetchProductData(productId, SHOP_DOMAIN);
    
    if (!productData) {
      console.error('No product data for item:', item);
      return null;
    }

    const productType = productData.productType || '';
    const deliveryTime = Number(productData.deliveryTime?.value || 0);
    const collections = productData.collections?.nodes || [];
    const firstVendorReceivingDate = collections.map(c => c.vendorReceivingDate?.value).find(v => !!v) || null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const isPreBooking = firstVendorReceivingDate && new Date(firstVendorReceivingDate) > today;
    
    let baseDate;
    if (productType === 'CN' && isPreBooking && firstVendorReceivingDate) {
      baseDate = new Date(firstVendorReceivingDate);
    } else {
      baseDate = new Date(today);
    }
    baseDate.setHours(0, 0, 0, 0);

    let startDate, endDate;
    
    if (productType === 'CN') {
      if (currentStitching.toLowerCase().includes('express stitching')) {
        startDate = addDays(baseDate, 8 + deliveryTime);
        endDate = addDays(baseDate, 9 + deliveryTime);
      } else if (currentStitching.toLowerCase().includes('standard stitching')) {
        startDate = addDays(baseDate, 19 + deliveryTime);
        endDate = addDays(baseDate, 21 + deliveryTime);
      } else {
        startDate = addDays(baseDate, 1 + deliveryTime);
        endDate = addDays(baseDate, 2 + deliveryTime);
      }
    } else if (productType === 'CD') {
      if (currentStitching.toLowerCase().includes('express stitching')) {
        startDate = addDays(today, deliveryTime + 8 + 2);
        endDate = addDays(today, deliveryTime + 9 + 2);
      } else if (currentStitching.toLowerCase().includes('standard stitching')) {
        startDate = addDays(today, deliveryTime + 19 + 2);
        endDate = addDays(today, deliveryTime + 21 + 2);
      } else {
        startDate = addDays(today, deliveryTime);
        endDate = addDays(today, deliveryTime + 1);
      }
    } else {
      startDate = addDays(baseDate, deliveryTime);
      endDate = addDays(baseDate, deliveryTime + 1);
    }

    return {
      estimatedDispatch: `(${formatDate(startDate)} - ${formatDate(endDate)})`,
      stitching: currentStitching
    };
  }

  async function updateSingleLineItem(itemKey, newDate, stitching, existingProperties) {
    try {
      // First, get the current cart to fetch the latest properties
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      const currentItem = cart.items.find(item => item.key === itemKey);
      
      if (!currentItem) {
        console.error(`Item ${itemKey} not found in cart`);
        return false;
      }
      
      // Preserve ALL existing properties and only update the ones we need
      const properties = {
        ...currentItem.properties, // Keep all existing properties
        "_Estimated Dispatch Date": newDate, // Update this one
        "_Stitching": stitching // Update this one
      };
      
      // Remove any undefined or null values
      Object.keys(properties).forEach(key => {
        if (properties[key] === undefined || properties[key] === null) {
          delete properties[key];
        }
      });

      const data = {
        id: itemKey,
        quantity: currentItem.quantity,
        properties: properties
      };

      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Failed to update item ${itemKey}:`, errorText);
        return false;
      }

      const result = await response.json();
      return true;

    } catch (error) {
      console.error(`Error updating item ${itemKey}:`, error);
      return false;
    }
  }

  async function fullCartUpdate() {
    if (isUpdating) {
      return;
    }

    isUpdating = true;

    try {
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      
      if (!cart.items || cart.items.length === 0) {
        return;
      }
      
      const linesWithUpdatedAttrs = [];

      for (const item of cart.items) {
        
        const newDate = await calculateItemDate(item);
        
        if (!newDate) {
          continue;
        }

        const success = await updateSingleLineItem(
          item.key,
          newDate.estimatedDispatch,
          newDate.stitching,
          item.properties
        );

        if (success) {
          linesWithUpdatedAttrs.push({
            id: item.key,
            attributes: [
              { key: "_Estimated Dispatch Date", value: newDate.estimatedDispatch },
              { key: "_Stitching", value: newDate.stitching }
            ]
          });
        }

        await new Promise(resolve => setTimeout(resolve, 500));
      }

      await new Promise(resolve => setTimeout(resolve, 2000));

      const verifyResponse = await fetch('/cart.js');
      const updatedCart = await verifyResponse.json();

      if (linesWithUpdatedAttrs.length > 0) {
        const dispatchInfo = computeMaxEstimatedDate(linesWithUpdatedAttrs);

        const stitchingType = dispatchInfo.hasStitching ? 
          (dispatchInfo.stitchingBreakdown?.toLowerCase().includes('express') ? 'express stitching' : 'standard stitching') 
          : 'none';

        let fullDateRange = dispatchInfo.maxDateStr;

        if (linesWithUpdatedAttrs.length > 0) {
        const maxDateLine = linesWithUpdatedAttrs.reduce((max, line) => {
            const lineEnd = extractEndDate(line.attributes.find(a => a.key === "_Estimated Dispatch Date")?.value || '');
            const maxEnd = max ? extractEndDate(max.attributes.find(a => a.key === "_Estimated Dispatch Date")?.value || '') : null;
            return (!maxEnd || (lineEnd && lineEnd > maxEnd)) ? line : max;
        }, linesWithUpdatedAttrs[0]);
        
        fullDateRange = maxDateLine.attributes.find(a => a.key === "_Estimated Dispatch Date")?.value || dispatchInfo.maxDateStr;
        
        const rangeEndDate = extractEndDate(fullDateRange);
        const maxDate = new Date(dispatchInfo.maxDateStr);
        
        if (rangeEndDate && maxDate && rangeEndDate.getTime() !== maxDate.getTime()) {
            const paren = /\(([^)]+)\)/.exec(fullDateRange);
            const source = paren ? paren[1] : fullDateRange;
            const parts = source.split("-").map((p) => p.trim());
            const startStr = parts.length === 2 ? parts[0] : source;
            
            fullDateRange = `(${startStr} - ${dispatchInfo.maxDateStr})`;
        }
        }

        const attributes = {
          estimated_dispatch: fullDateRange,
          estimated_dispatch_sc: dispatchInfo.maxDateStr,
          has_stitching: dispatchInfo.hasStitching ? 'true' : 'false',
          stitching_type: stitchingType,
          stitching_count: String(dispatchInfo.stitchedCount || 0),
          stitching_breakdown: dispatchInfo.stitchingBreakdown || '',
          has_customization: dispatchInfo.hasCustomization ? 'true' : 'false',
          customization_breakdown: dispatchInfo.customizationBreakdown || ''
        };

        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes })
        });
      }

    } catch (error) {
      console.error('Error in full cart update:', error);
    } finally {
      isUpdating = false;
    }
  }

  function initialize() {
    setTimeout(fullCartUpdate, 3000);

    document.addEventListener('cart:updated', () => setTimeout(fullCartUpdate, 2000));
    document.addEventListener('cart:change', () => setTimeout(fullCartUpdate, 2000));
    document.addEventListener('cart:added', () => setTimeout(fullCartUpdate, 2000));
    document.addEventListener('cart:removed', () => setTimeout(fullCartUpdate, 2000));

    document.addEventListener('change', (e) => {
      if (e.target.matches('input[type="radio"][name^="hulk_radio_"]')) {
        setTimeout(fullCartUpdate, 2000);
      }
    });

    document.addEventListener('click', (e) => {
        const prebookBtn = e.target.closest('.prebook-btn');
        if (prebookBtn) {
            setTimeout(fullCartUpdate, 3000);
        }
    });

    document.addEventListener('click', (e) => {
        const addToCartBtn = e.target.closest('.add-to-cart');
        if (addToCartBtn && !addToCartBtn.classList.contains('prebook-btn')) {
            setTimeout(fullCartUpdate, 3000);
        }
    });

    document.addEventListener('click', (e) => {
        const checkoutBtn = e.target.closest('.cart__checkout');
        if (checkoutBtn) {
            fullCartUpdate().then(() => {
                console.log('Cart updated before checkout');
            }).catch(err => {
                console.error('Error updating cart before checkout:', err);
            });
        }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize();
  }

  window.forceCartUpdate = fullCartUpdate;

})();